import { WizDatasource } from '@wizeoo/akm-component-lib';
import { FIELD_KIND } from '@wizeoo/akm-compute-lib';
import { WidgetConfigurationObj, WidgetConfigurationItemObj } from '@wizeoo/akm-message-lib';

import { ViewData } from '../../../model/view.data';
import { ComparatorVO } from '../../../vo/common/tool/comparator.vo';
import { FreedataArgsVO } from  '../../../vo/core/freedataargs.vo';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';
import { COMPARATOR } from "../../../model/enums";
import { Model } from '../../../model/model';
import { FreedataTimelineWidget } from './freedata-timeline.widget';

export interface FreedataTimelineFilterItemObj {
  pfo: FreedataTimelineFilterObj // le parent
  code: any; // la valeur du champ
  label: string; // le libellé correspondant au code
}


/**
 * l'objet pour alimenter un champ de filtre
 * contient la référence à la FEI, et au WidgetConfigurationItemObj
 */
export class FreedataTimelineFilterObj {
  
  
  public pivot: FreedataTimelineWidget; // le pivot source
  public wco: WidgetConfigurationObj; // la configuration globale
  public wcio: WidgetConfigurationItemObj; // la configuration du champs

  public arg: FreedataArgsVO;

  public label: string; // le rendu intelligible (pour l'entête du filtre)

  public filterDS: WizDatasource<FreedataTimelineFilterItemObj> = new WizDatasource<FreedataTimelineFilterItemObj>()
    .setSelectionMode('multiple')
    .build(); // la source de donnée de la combo
  public hasFilter: boolean = false; // au moins un élément de filtre (pour activation delete)

  public exp: string = null; // sur filtre numérique, l'expression
  public comparatorCode: string = null; // accesseur sur le comparateur en cas d'expression
  public sel: string = null; // sélection mémorisée
  public datakind: string = FIELD_KIND.STRING; // le type de données (champ sélectionné) du filter


  constructor(pivot: FreedataTimelineWidget, wcio: WidgetConfigurationItemObj) {
    this.pivot = pivot;
    this.wco = pivot.context;
    this.wcio = wcio;
    this.build();
    
  }

  public get model(): Model {
    return this.pivot.model;
  }

  /** retourne les valeurs des éléments sélectionnés sous forme de string */
  public get selectedAsString(): string[] {
    return this.filterDS.selectedItems.map((item) => item.code);
  }

  /** retourne les valeurs des éléments sélectionnés sous forme de string, libellé pour rendu */
  public get selectedAsLabel(): string {
    return this.filterDS.selectedItems.map((item) => item.label).join(';');
  }

  /**
   * retourne la part d'expression formatée
   * @param part string
   * @return le même formaté
   */
  private formatPart(part: string): string {
    let result: string = part;
    result = this.model.formatEngine.applyFormat(part, this.arg.dataType, this.arg.format, null);
    return result;
  }


  /**
   * retourne l'expression formatée
   */
  public get expAsLabel(): string {
    let result: string = ' ';

    const parts: string[] = this.exp.split(ViewData.KEY_SEPARATOR);
    const comp: ComparatorVO = ViewData.getInstance().getExtComparator(parts[0]);
    const formattedParts: string[] = [];
    if(!!comp) {
      switch(comp.nbParams) {
        case 0: 
          formattedParts.push(comp.fulltrsLabel);
        break;
        case 1: 
          formattedParts.push(comp.shortLabel);
          formattedParts.push(this.formatPart(parts[1]));
        break;
        case 2: 
          formattedParts.push(this.formatPart(parts[1]));
          formattedParts.push(comp.shortLabel);
          formattedParts.push(this.formatPart(parts[2]));
        break;
      }
    }

    result = formattedParts.join(' ');
    return result;
  }

  /**
   * construction d'un item de rendu
   * @param key 
   * @param map 
   * @returns 
   */
  private buildItem(key: string, map:  Map<any, string>) : FreedataTimelineFilterItemObj {
    return {pfo: this , code: key, label: map.get(key)}
  }

  // construction des autres données ,point d'entrée pour refresh sur changement du pool de données
  public build(): void {

    this.arg = this.model.freedataEngine.getArgFromFD(this.wco.fdk.split(ViewData.CONTEXT_SEPARATOR)[0], this.wcio.fdvf);
    if(!this.arg) {
      console.log('missing arg:', this.wcio.fdvf);
      return;
    }
    
    this.label = this.wcio.lbl ? this.wcio.lbl : this.arg.trsLabel; // le label de la combo

    this.datakind = this.arg.dataType; // prise du type, peut être écrasé par les params de FDs
    this.exp = this.wcio.filtExp;
    this.sel = this.wcio.sel;
    // peu importe les doublons. Ici, on veut la valeur de la baseline pour filtrer.
    let valMap: Map<any, string> = new Map();

    this.pivot.fdvDS.getSource().forEach((fdv: FreedataValueVO) => {
      valMap.set(fdv.argsObjVO[this.arg.code], this.model.formatEngine.applyFormat(fdv.argsObjVO[this.arg.code], this.arg.dataType, this.arg.format, null ));
    });
 
    valMap.delete(undefined); // on supprimer undefined
    valMap.delete(''); // on supprimer vide
    valMap.delete(null); // on supprimer null


    const filterItems: FreedataTimelineFilterItemObj[] = Array.from(valMap.keys()).map(
      str => this.buildItem(str, valMap) );


    this.filterDS.setSortFields([{ property: 'code', numeric: this.model.dataEngine.datakindIsNumeric(this.arg.dataType), way: 'asc' }]);
    this.filterDS.setSource(filterItems); // alimentation de la DS
 
    this.applyFilterSelection();
  }

  public applyFilterSelection(): void {
    
    let selectPackMap: Map<string, FreedataTimelineFilterItemObj> = new Map();
    if (!!this.exp) { // filtre par expression
      this.hasFilter = true; // j'ai un filtre... même s'il fitre rien...
      this.comparatorCode = this.exp.split(ViewData.KEY_SEPARATOR)[0];
      selectPackMap = this.applyExp();
      // console.log('filter exp', selectPackMap);
    } else if (this.sel) { // collecte des élements de contexte pour la sélection  
      // flag pour déclenchement de la filterFunc du pivotComponent
      const valArr: string[] = this.sel.split(ViewData.KEY_SEPARATOR);

      // nettoyage des selected morts (plus de valeur dans le grid)
      valArr.forEach((s) => {
        const item = this.filterDS.getItemByKey(s);
        if (item && item !== undefined) {
          this.hasFilter = true;
          selectPackMap.set(item.code, item);
          // selected.push(item);
        }
      });
    } 

    if(this.hasFilter) {
      this.filterDS.setSelectedItems([...selectPackMap.values()]);
    } else {
      this.filterDS.clearSelection();
    }
    
  }


  /**
   * retourne soit l'expression, soit la liste des valeurs (formatées), soit rien
   */
  public get trueLabel(): string {
    if (this.exp) {
      return this.expAsLabel;
    } else {
      return this.selectedAsLabel;
    }
  }

 /** application de l'expression sur le filtre */
 private applyExp(): Map<string, FreedataTimelineFilterItemObj> {
  let goodItems: Map<string, FreedataTimelineFilterItemObj> = new Map();

  const parts: string[] = this.exp.split(ViewData.KEY_SEPARATOR);
  const comp: ComparatorVO = ViewData.getInstance().getExtComparator(parts[0]);
  const partOne: any = parts[1];
  const partTwo: any = parts.length > 2 ? parts[2] : null;

  this.filterDS.getSource().forEach((pfeo: FreedataTimelineFilterItemObj) => {
    // console.log('applyExp', pfeo.rawVal, comp.code, partOne, partTwo );
    switch(comp.code) {
      case COMPARATOR.INF:
        if (Number(pfeo.code) < Number(partOne)) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;

      case COMPARATOR.INFEQUAL:
        if (Number(pfeo.code) <= Number(partOne)) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.EQUAL:
        if (pfeo.code === partOne) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.NOTEQUAL:
        if (pfeo.code !== partOne) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.SUP:
        if (Number(pfeo.code) > Number(partOne)) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.SUPEQUAL:
        if (Number(pfeo.code) >= Number(partOne)) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.BET:
        if (Number(pfeo.code) >= Number(partOne) && Number(pfeo.code) <= Number(partTwo)) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.EMPTY:
        if (!pfeo.code) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.NOTEMPTY:
        if (!!pfeo.code) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.STARTWITH:
        if (pfeo.code?.toUpperCase()?.startsWith(partOne?.toUpperCase())) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
      case COMPARATOR.CONTAINS:
        if (pfeo.code?.toUpperCase()?.indexOf(partOne?.toUpperCase()) > -1) {
          goodItems.set(pfeo.code, pfeo);
        }
        break;
    }
  });

  return goodItems;
}


}
